import React, { useRef, useEffect } from "react";
import { useLocation } from "react-router";
import { AppShell, Center, Text, rem } from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { Header, NavbarLinks } from "../../components";

const HEADER_HEIGHT = 50;
const FOOTER_HEIGHT = 36;
const NAVBAR_WIDTH = 240;

// Larger than any viewport → navbar is always an overlay drawer, never a
// permanent desktop panel, so the burger can open it at any width.
const NAV_OVERLAY_BREAKPOINT = "9999em";

// Widest viewport at which the horizontal top-nav still fits, per locale.
const NAV_BREAKPOINT = { ru: 1540, default: 1320 };

export default function SideNavOuterToolbar({ children }) {
  const contentRef = useRef(null);
  const [navbarOpened, { close: closeNavbar, toggle: toggleNavbar }] =
    useDisclosure(false);
  const location = useLocation();

  // Fall back to the burger + sidenav below the per-locale cutoff
  // (e.g. "ru-RU" -> "ru").
  const { i18n } = useTranslation();
  const lang = i18n.language?.split("-")[0];
  const bp = NAV_BREAKPOINT[lang] ?? NAV_BREAKPOINT.default;
  const compact = useMediaQuery(`(max-width: ${bp}px)`, false, {
    getInitialValueInEffect: false,
  });

  // Scroll to top on route change (replicates old ScrollView behaviour)
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0 });
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!compact && navbarOpened) closeNavbar();
  }, [compact, navbarOpened, closeNavbar]);

  return (
    <AppShell
      header={{ height: HEADER_HEIGHT }}
      footer={{ height: FOOTER_HEIGHT }}
      navbar={{
        width: NAVBAR_WIDTH,
        breakpoint: NAV_OVERLAY_BREAKPOINT,
        collapsed: { desktop: !navbarOpened, mobile: !navbarOpened },
      }}
      styles={{
        header: {
          background: "#191D4A",
          borderBottom: "none",
          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        },
        navbar: {
          borderRight: "1px solid #e8e8f0",
          background: "#191D4A",
          overflowY: "auto",
        },
        main: {
          background: "var(--mantine-color-gray-0)",
        },
        footer: {
          background: "#191D4A",
          borderTop: "none",
          boxShadow: "0 -2px 8px rgba(0,0,0,0.2)",
        },
      }}
    >
      <AppShell.Header>
        <Header
          navbarOpened={navbarOpened}
          onNavbarToggle={toggleNavbar}
          compact={compact}
        />
      </AppShell.Header>

      {/* Navbar — shown on small/medium screens when burger is open */}
      <AppShell.Navbar>
        <NavbarLinks onNavigate={closeNavbar} />
      </AppShell.Navbar>

      <AppShell.Main>
        <div
          ref={contentRef}
          style={{
            height: `calc(100vh - ${rem(HEADER_HEIGHT)} - ${rem(FOOTER_HEIGHT)})`,
            overflowY: "auto",
            overflowX: "auto",
            scrollbarGutter: "stable",
            padding:"20px auto"
          }}
        >
          {children}
        </div>
      </AppShell.Main>

      <AppShell.Footer>
        <Center h="100%">
          <Text c="white" size="sm">
            ©{new Date().getFullYear()} TRM
          </Text>
        </Center>
      </AppShell.Footer>
    </AppShell>
  );
}
